import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Typography } from '../../../display/Typography';
import { MainButton } from '../../../inputs/buttons/MainButton';

// Icons
import AlertIcon from '../../../../assets/icons/alert-icon.svg';

// Styled — contenedor, icono y opción destacada, los mismos del de segundo
// plano: es la misma situación (falta un permiso y no se puede seguir) y tiene
// que verse igual. Lo propio de este modal son los pasos y el aviso.
import {
  IconImg,
  TextGroup,
  OptionChip,
} from '../BackgroundLocationRequiredModal/styles';
import { ScrollableContainer, Steps, Notice } from './styles';

// Types
import { PhotoPermissionStep, PhotoPermissionsRequiredModalProps } from './types';

/**
 * Textos que cambian cuando el operario ya eligió la opción que no sirve. En
 * la ubicación, tras elegir «aproximada», el diálogo de Android ofrece
 * «Cambiar a ubicación precisa» y en Ajustes hay que activar el interruptor
 * «Ubicación precisa». En las fotos la opción buena es la misma.
 */
const WRONG_CHOICE_KEYS: Record<PhotoPermissionStep, Record<string, string>> = {
  location: {
    askOption: 'askOptionWrongChoice',
    settingsStep3: 'settingsStep3WrongChoice',
    settingsOption: 'settingsOptionWrongChoice',
  },
  photos: {},
};

/**
 * Modal bloqueante que exige los permisos que necesita una foto para guardar
 * dónde se hizo: primero la ubicación precisa (cámara de ROIS) y luego la
 * ubicación de las fotos (galería). Spec 2026-09-23 fotos geolocalizadas,
 * § 14 y § 15. Lo abre `usePhotoPermissionsGate`, de uno en uno.
 *
 * ─── Cómo está escrito (y por qué) ─────────────────────────────────────────
 *
 * - **Primero para qué sirve**, en una frase y con el beneficio, no con el
 *   mecanismo.
 * - **Luego pasos numerados**, y justo debajo la opción que hay que tocar,
 *   destacada, con la etiqueta exacta que pinta Android. Va en su propio
 *   recuadro y no dentro del texto del paso: dentro de una línea el recuadro
 *   se partía en dos (visto en la tablet).
 * - 🔴 **Nunca se nombra la opción que no sirve.** La primera versión decía
 *   «elige esta opción («Permitir acceso limitado» no sirve):» y, leyendo en
 *   orden, lo primero tras «elige» era justo la mala (lo cazó el usuario en la
 *   tablet, 24-09-2026). Decir qué hacer, no qué no hacer.
 * - **Si el operario ya eligió la opción mala**, entonces sí se le dice, y
 *   solo entonces: qué eligió y qué pasa por eso, antes de los pasos.
 *
 * ─── Por qué bloquea ───────────────────────────────────────────────────────
 *
 * Con el flag del tenant encendido, la ubicación de la foto es parte de la
 * prueba del trabajo. Sin X ni Cancelar, igual que
 * `BACKGROUND_LOCATION_REQUIRED`. Bloquear es honesto porque el botón SIEMPRE
 * funciona: el diálogo mientras Android lo enseñe, y Ajustes cuando ya no.
 */
export const PhotoPermissionsRequiredModal = ({
  data,
}: PhotoPermissionsRequiredModalProps): JSX.Element => {
  const { t } = useTranslation(['modals']);
  const inSettings = data.mode === 'settings';
  const text = (name: string): string => {
    const override = data.wrongChoice ? WRONG_CHOICE_KEYS[data.step][name] : undefined;
    return t(`modals:photoPermissions.${data.step}.${override ?? name}`);
  };

  return (
    <ScrollableContainer>
      <IconImg src={AlertIcon} alt="" />
      <TextGroup>
        <Typography size="L" variant="semiBold">
          {text('title')}
        </Typography>
        <Typography size="M">{text('reason')}</Typography>
      </TextGroup>

      {data.wrongChoice && (
        <Notice role="status">{text('wrongChoiceNotice')}</Notice>
      )}

      <TextGroup>
        {inSettings && <Typography size="M">{text('settingsIntro')}</Typography>}
        <Steps>
          {inSettings ? (
            <>
              <li>{text('settingsStep1')}</li>
              <li>{text('settingsStep2')}</li>
              <li>{text('settingsStep3')}</li>
            </>
          ) : (
            <>
              <li>{text('askStep1')}</li>
              <li>{text('askStep2')}</li>
            </>
          )}
        </Steps>
      </TextGroup>

      <OptionChip>{inSettings ? text('settingsOption') : text('askOption')}</OptionChip>

      <MainButton
        text={
          inSettings
            ? t('modals:photoPermissions.openSettings')
            : t('modals:photoPermissions.continue')
        }
        onClick={inSettings ? data.onOpenSettings : data.onRequest}
      />
    </ScrollableContainer>
  );
};

export default PhotoPermissionsRequiredModal;
